import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { AlertCircle, Eye, Loader2, Save, Search, Lock } from 'lucide-react';
import { ViewProfileDialog } from '@/components/profile';
import { useLeaveBalances, useLeavePolicyMutations } from '@/hooks/leave';
import { useTeamMembers } from '@/hooks/team';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission } from '@/hooks/permissions';
import type { LeaveBalanceWithProfile } from '@/services/leave/leavePolicyTypes';
import type { TeamMember } from '@/services/team/types';
import { generateDisplayName, generateInitials } from '@/utils/avatarUtils';
import { cn } from '@/lib/utils';

type AdjustmentInputs = Record<string, { days?: string; hours?: string; reason?: string }>;
type RowErrors = Record<string, string>;

const roleStyles: Record<string, string> = {
  ADMIN: 'bg-red-100 text-red-800 border-red-200',
  MANAGER: 'bg-blue-100 text-blue-800 border-blue-200',
  SUPERVISOR: 'bg-purple-100 text-purple-800 border-purple-200',
  STAFF: 'bg-green-100 text-green-800 border-green-200'
};

const formatNumber = (value?: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(2)) : 0;

export const EmployeeManagementSection = () => {
  const { data: teamMembers = [], isLoading: membersLoading } = useTeamMembers();
  const { updateLeaveBalance } = useLeavePolicyMutations();
  const { profile: currentUserProfile } = useAuth();
  
  // Permission-based access control (replaces legacy role check)
  const canEditBalances = useHasPermission('manage_employee_pay');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [viewProfileOpen, setViewProfileOpen] = useState(false);
  const [profileTarget, setProfileTarget] = useState<TeamMember | null>(null);
  const [adjustments, setAdjustments] = useState<AdjustmentInputs>({});
  const [rowErrors, setRowErrors] = useState<RowErrors>({});
  const [updatingBalanceId, setUpdatingBalanceId] = useState<string | null>(null);

  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return teamMembers;
    const term = searchTerm.toLowerCase();
    return teamMembers.filter(member => {
      const name = generateDisplayName(member.first_name, member.last_name).toLowerCase();
      return name.includes(term) || member.email.toLowerCase().includes(term);
    });
  }, [searchTerm, teamMembers]);

  useEffect(() => {
    if (!selectedMemberId && filteredMembers.length > 0) {
      setSelectedMemberId(filteredMembers[0].id);
    } else if (selectedMemberId && filteredMembers.length > 0) {
      const stillVisible = filteredMembers.some(member => member.id === selectedMemberId);
      if (!stillVisible) {
        setSelectedMemberId(filteredMembers[0].id);
      }
    } else if (filteredMembers.length === 0) {
      setSelectedMemberId(null);
    }
  }, [filteredMembers, selectedMemberId]);

  const selectedMember = filteredMembers.find(member => member.id === selectedMemberId) || null;

  const {
    data: balances = [],
    isLoading: balancesLoading
  } = useLeaveBalances(selectedMember?.id, {
    enabled: !!selectedMember?.id
  });

  const handleViewProfile = (member: TeamMember) => {
    setProfileTarget(member);
    setViewProfileOpen(true);
  };

  const setAdjustmentValue = (
    balanceId: string,
    field: 'days' | 'hours' | 'reason',
    value: string
  ) => {
    setAdjustments(prev => ({
      ...prev,
      [balanceId]: {
        ...prev[balanceId],
        [field]: value
      }
    }));
    setRowErrors(prev => ({ ...prev, [balanceId]: '' }));
  };

  const handleUpdateBalance = (balance: LeaveBalanceWithProfile) => {
    const adjustment = adjustments[balance.id] || {};
    const hasDaysInput = adjustment.days !== undefined && adjustment.days !== '';
    const hasHoursInput = adjustment.hours !== undefined && adjustment.hours !== '';
    const hasReason = adjustment.reason !== undefined && adjustment.reason.trim() !== '';

    if (!hasDaysInput && !hasHoursInput) {
      setRowErrors(prev => ({ ...prev, [balance.id]: 'Enter a days or hours adjustment' }));
      return;
    }

    if (!hasReason) {
      setRowErrors(prev => ({ ...prev, [balance.id]: 'A reason is required for balance adjustments' }));
      return;
    }

    const parsedDays = hasDaysInput ? Number(adjustment.days) : undefined;
    const parsedHours = hasHoursInput ? Number(adjustment.hours) : undefined;

    if (
      (parsedDays !== undefined && (!Number.isFinite(parsedDays) || parsedDays === 0)) ||
      (parsedHours !== undefined && (!Number.isFinite(parsedHours) || parsedHours === 0))
    ) {
      setRowErrors(prev => ({
        ...prev,
        [balance.id]: 'Use a non-zero numeric value for adjustments'
      }));
      return;
    }

    setUpdatingBalanceId(balance.id);
    setRowErrors(prev => ({ ...prev, [balance.id]: '' }));

    updateLeaveBalance.mutate(
      {
        balance_id: balance.id,
        adjustment_days: parsedDays,
        adjustment_hours: parsedHours,
        reason: adjustment.reason?.trim()
      },
      {
        onSuccess: () => {
          setAdjustments(prev => ({
            ...prev,
            [balance.id]: { days: '', hours: '', reason: '' }
          }));
        },
        onError: (error: Error) => {
          setRowErrors(prev => ({
            ...prev,
            [balance.id]: error.message || 'Failed to update balance'
          }));
        },
        onSettled: () => setUpdatingBalanceId(null)
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Employee Management</CardTitle>
        <CardDescription>
          View employee profiles and manually adjust their leave balances.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Employee Directory</p>
                <p className="text-sm text-muted-foreground">Select an employee to manage</p>
              </div>
              <Badge variant="outline">{teamMembers.length} total</Badge>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name or email"
                className="pl-9"
              />
            </div>

            <Separator />

            <ScrollArea className="h-[420px]">
              <div className="space-y-2 pr-2">
                {membersLoading &&
                  Array.from({ length: 5 }).map((_, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                      <Skeleton className="h-6 w-16" />
                    </div>
                  ))}

                {!membersLoading && filteredMembers.length === 0 && (
                  <div className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                    <AlertCircle className="h-4 w-4" />
                    No employees match your search.
                  </div>
                )}

                {!membersLoading &&
                  filteredMembers.map(member => (
                    <div
                      key={member.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedMemberId(member.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedMemberId(member.id);
                        }
                      }}
                      className={cn(
                        'flex w-full items-center justify-between rounded-lg border p-3 text-left transition hover:border-primary cursor-pointer',
                        selectedMemberId === member.id && 'border-primary/70 bg-primary/5'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.avatar_url || undefined} />
                          <AvatarFallback>{generateInitials(member.first_name, member.last_name)}</AvatarFallback>
                        </Avatar>
                        <div className="space-y-0.5">
                          <p className="font-medium leading-none">{generateDisplayName(member.first_name, member.last_name)}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="font-medium capitalize">{member.role?.toLowerCase()}</span>
                            {member.status === 'archived' && (
                              <Badge variant="outline" className="text-amber-700">
                                Archived
                              </Badge>
                            )}
                            {(!member.first_name || !member.last_name) && (
                              <Badge variant="outline" className="text-blue-700">
                                Pending profile
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={e => {
                          e.stopPropagation();
                          handleViewProfile(member);
                        }}
                        aria-label={`View ${generateDisplayName(member.first_name, member.last_name)} profile`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">Leave balances</p>
                <p className="text-sm text-muted-foreground">
                  {selectedMember
                    ? `Manage balances for ${generateDisplayName(selectedMember.first_name, selectedMember.last_name)}`
                    : 'Select an employee to view balances'}
                </p>
              </div>
              {selectedMember && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Role</span>
                  <Badge
                    variant="outline"
                    className={roleStyles[selectedMember.role] || 'bg-muted text-foreground'}
                  >
                    {selectedMember.role || 'Unknown'}
                  </Badge>
                </div>
              )}
            </div>

            <Card className="mt-4">
              <CardContent className="p-0">
                {!selectedMember && (
                  <div className="flex flex-col items-center justify-center space-y-2 py-12 text-center text-muted-foreground">
                    <AlertCircle className="h-5 w-5" />
                    <p className="text-sm">Choose an employee to view their leave balances.</p>
                  </div>
                )}

                {selectedMember && (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[260px]">Leave type</TableHead>
                          <TableHead>Current balance</TableHead>
                          <TableHead>Adjustment</TableHead>
                          <TableHead className="w-[140px] text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {balancesLoading && (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <div className="space-y-3 p-4">
                                <Skeleton className="h-5 w-1/3" />
                                <div className="grid grid-cols-3 gap-3">
                                  <Skeleton className="h-10" />
                                  <Skeleton className="h-10" />
                                  <Skeleton className="h-10" />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}

                        {!balancesLoading && balances.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                                <AlertCircle className="h-4 w-4" />
                                No leave balances found for this employee.
                              </div>
                            </TableCell>
                          </TableRow>
                        )}

                        {!balancesLoading &&
                          balances.map(balance => {
                            const adjustment = adjustments[balance.id] || { days: '', hours: '', reason: '' };
                            const errorMessage = rowErrors[balance.id];

                            return (
                              <TableRow key={balance.id} className="align-top">
                                <TableCell>
                                  <div className="space-y-1">
                                    <p className="font-medium">{balance.policy.name}</p>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                                      {balance.policy.leave_type}
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline">Days</Badge>
                                      <span className="font-medium">{formatNumber(balance.current_balance_days)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline">Hours</Badge>
                                      <span className="font-medium">{formatNumber(balance.current_balance_hours)}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      Pending: {formatNumber(balance.pending_days)} days /{' '}
                                      {formatNumber(balance.pending_hours)} hours
                                    </p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {canEditBalances ? (
                                    <div className="space-y-2">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="text-xs text-muted-foreground">
                                            Days adjustment
                                          </label>
                                          <Input
                                            type="number"
                                            step="0.25"
                                            value={adjustment.days ?? ''}
                                            onChange={e => setAdjustmentValue(balance.id, 'days', e.target.value)}
                                            placeholder="e.g. 1.5"
                                          />
                                        </div>
                                        <div>
                                          <label className="text-xs text-muted-foreground">
                                            Hours adjustment
                                          </label>
                                          <Input
                                            type="number"
                                            step="0.25"
                                            value={adjustment.hours ?? ''}
                                            onChange={e => setAdjustmentValue(balance.id, 'hours', e.target.value)}
                                            placeholder="e.g. 4"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-xs text-muted-foreground">
                                          Reason <span className="text-destructive">*</span>
                                        </label>
                                        <Input
                                          type="text"
                                          value={adjustment.reason ?? ''}
                                          onChange={e => setAdjustmentValue(balance.id, 'reason', e.target.value)}
                                          placeholder="e.g. Correction for missed accrual"
                                        />
                                      </div>
                                      {errorMessage && (
                                        <p className="text-xs text-destructive">{errorMessage}</p>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                      <Lock className="h-4 w-4" />
                                      <span>Admin access required</span>
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {canEditBalances ? (
                                    <Button
                                      size="sm"
                                      onClick={() => handleUpdateBalance(balance)}
                                      disabled={updateLeaveBalance.isPending}
                                    >
                                      {updateLeaveBalance.isPending && updatingBalanceId === balance.id ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      ) : (
                                        <Save className="mr-2 h-4 w-4" />
                                      )}
                                      Update
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">View only</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>

      {profileTarget && (
        <ViewProfileDialog
          open={viewProfileOpen}
          onOpenChange={setViewProfileOpen}
          teamMember={profileTarget}
        />
      )}
    </Card>
  );
};

export default EmployeeManagementSection;
